import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CifradoAES, CifradoRSA } from "../../components/FuncionesCifrado";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { BASE_URL } from '../../config/api';

const SubirArchivosIndex = ({ tokenSeguridad, publicKeyRSA, datosCarga, startUpload, archivoAgregado, validateUploads }) => {
    // State management
    const [fileList, setFileList] = useState([]);
    const [filesToUpload, setFilesToUpload] = useState([]);
    const [uploadStats, setUploadStats] = useState({
        uploaded: 0,
        total: 0,
        isUploading: false,
        currentProgress: 0,
        currentFileName: ''
    });
    const [concurrentUploads, setConcurrentUploads] = useState(0);
    const [alertaDuplicados, setAlertaDuplicados] = useState({
        visible: false,
        archivos: []
    });
    const [error, setError] = useState(null);
    const [carpetasExpandidas, setCarpetasExpandidas] = useState(new Set());
    const [bytesTotales, setBytesTotales] = useState(0);
    
    // Refs for DOM elements
    const containerRef = useRef(null);
    const fileInputRef = useRef(null);
    const fileAreaRef = useRef(null);
    const uploadWindowRef = useRef(null);
    const uploadListRef = useRef(null);
    const folderInputRef = useRef(null);
    const folderAreaRef = useRef(null);
    const folderIconRef = useRef(null);
    const overlayRef = useRef(null);
    const prevactiveTab = useRef();
    const datosCargaRef = useRef(datosCarga);
    
    // Constants
    const MAX_CONCURRENT_UPLOADS = 3;
    const RETRY_ATTEMPTS = 3;
    const RETRY_DELAY = 3000;
    const MAX_FILE_SIZE = 5000 * 1024 * 1024;
    const MAX_TOTAL_SIZE = 10000 * 1024 * 1024;
    const CHUNK_SIZE = 1024 * 1024 * 5; // 5MB chunks
    const LARGE_FILE_THRESHOLD = 1024 * 1024 * 50; // 50MB threshold

    const aesEncryption = new CifradoAES();
    const rsaEncryption = new CifradoRSA();

    // Initialize folder input attributes
    useEffect(() => {
        if (folderInputRef.current) {
            const folder = folderInputRef.current;
            folder.setAttribute("webkitdirectory", "");
            folder.setAttribute("mozdirectory", "");
            folder.setAttribute("msdirectory", "");
            folder.setAttribute("odirectory", "");
            folder.setAttribute("directory", "");
        }
    }, []);

    // Start upload process when files are added
    useEffect(() => {
        datosCargaRef.current = datosCarga;
        //if (uploadStats.total > 0 && !uploadStats.isUploading && startUpload) {
        if (startUpload && datosCarga) {
            console.log("datosCarga",datosCarga);
            startUploadProcess();
        }
    }, [startUpload, datosCarga]);
    useEffect(() => {
        if (bytesTotales > 0 ) {
            archivoAgregado?.(uploadStats.total, bytesTotales);
        }
    }, [uploadStats.total, bytesTotales]);

    // Utility functions
    const formatBytes = useCallback((bytes, decimals = 2) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }, []);

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // File handling
    const handleFileSelection = useCallback(async (e) => {
        try {
            // Reset alerts
            setAlertaDuplicados({ visible: false, archivos: [] });
            setError(null);

            // Extract files from either event or direct file list
            const tempFileList = e.target?.files || e;
            
            if (!tempFileList || tempFileList.length === 0) {
                setError('No se seleccionaron archivos');
                return;
            }
            /* const files = event.target?.files || event;
            if (!files?.length) return;  */
            // Create a Set for faster duplicate checking
            const existingFilesSet = new Set(
                fileList.map(file => `${file.name}:${file.size}`)
            );
            
            // Generate timestamp ID using ISO format
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            
            const newFilesToUpload = [];
            const newFileList = [];
            const duplicados = [];
            const archivosGrandes = [];
            let nuevoBytesTotales = bytesTotales;           

            // Process each file
            Array.from(tempFileList).forEach(file => {
                // Validate file size
                if (file.size > MAX_FILE_SIZE) {
                    archivosGrandes.push(`${file.name} (${formatBytes(file.size)})`);
                    return;
                }
                
                // Check for duplicates using Set
                const fileKey = `${file.name}:${file.size}`;
                if (existingFilesSet.has(fileKey)) {
                    duplicados.push(file.name);
                    return;
                }
                
                // Check total size limit
                if (nuevoBytesTotales + file.size > MAX_TOTAL_SIZE) {
                    setError(`El tamaño total excedería el límite de ${formatBytes(MAX_TOTAL_SIZE)}`);
                    return;
                }                
        
                // Add new 
                // Create unique ID for each file
                const fileId = `${timestamp}:${uuidv4()}`;
                const fullPath = file.webkitRelativePath || file.name;
                
                newFilesToUpload.push({ 
                    file, 
                    type: "Archivo",
                    id: fileId,
                    fullPath: fullPath
                });
                newFileList.push({
                    id: fileId,
                    size: file.size,
                    name: file.name,
                    type: "Archivo",
                    status: 'pending' // pending, uploading, success, error
                });
                nuevoBytesTotales += file.size;
                existingFilesSet.add(fileKey); // Add to Set to prevent duplicates within same batch
            });
                
            // Show alerts if needed
            if (duplicados.length > 0) {
                setAlertaDuplicados({
                    visible: true,
                    archivos: duplicados
                });
            }
            
            if (archivosGrandes.length > 0) {
                setError(`Los siguientes archivos exceden el tamaño máximo de ${formatBytes(MAX_FILE_SIZE)}: ${archivosGrandes.join(', ')}`);
            }
            
            // Update state with new files
            if (newFileList.length > 0) {
                setFilesToUpload(newFilesToUpload);
                setFileList(prev => [...prev, ...newFileList]);
                setUploadStats(prev => ({
                    ...prev,
                    total: prev.total + newFilesToUpload.length
                }));
                setBytesTotales(nuevoBytesTotales);
            }
        } catch (err) {
            console.error('Error al agregar archivos:', err);
            setError('Ocurrió un error al procesar los archivos. Por favor, intenta de nuevo.');
        } finally {
            // Reset input value to allow selecting the same folder again
            if (e.target) {
                e.target.value = '';
            }
        }
    }, []);

    const handleFolderSelect = async (e) => {
        
        try {
            setAlertaDuplicados({ visible: false, archivos: [] });
            setError(null);
    
            const tempFileList = e.target?.files || e;
            //console.log(fileList);
            if (!tempFileList || tempFileList.length === 0) {
                setError('No se seleccionaron archivos');
                return;
            }
    
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            
            // Group files by folder structure
            const carpetasMap = new Map();
            const archivosIndividuales = [];
            
            Array.from(tempFileList).forEach(file => {
                // Extract folder path from webkitRelativePath or file path
                const fullPath = file.webkitRelativePath || file.name;
                const pathParts = fullPath.split('/');
                
                if (pathParts.length > 1) {
                    // It's inside a folder
                    const folderName = pathParts[0];
                    
                    if (!carpetasMap.has(folderName)) {
                    carpetasMap.set(folderName, {
                        nombre: folderName,
                        archivos: [],
                        tamanoTotal: 0
                    });
                    }
                    
                    const carpeta = carpetasMap.get(folderName);
                    carpeta.archivos.push({
                    file,
                    relativePath: pathParts.slice(1).join('/'),
                    size: file.size
                    });
                    carpeta.tamanoTotal += file.size;
                } else {
                    // Individual file
                    archivosIndividuales.push(file);
                }
            });
    
            const existingFilesSet = new Set(
            fileList
                .filter(item => item.type === 'Archivo')
                .map(file => `${file.name}:${file.size}`)
            );
    
            const existingFoldersSet = new Set(
            fileList
                .filter(item => item.type === 'Carpeta')
                .map(folder => folder.name)
            );
    
            const newFilesToUpload = [];
            const newFileList = [];
            const duplicados = [];
            const archivosGrandes = [];
            let nuevoBytesTotales = bytesTotales;
    
            // Process folders
            carpetasMap.forEach((carpeta, folderName) => {
                // Check for duplicate folder
                if (existingFoldersSet.has(folderName)) {
                    duplicados.push(`Carpeta: ${folderName}`);
                    return;
                }
        
                // Check total size limit
                if (nuevoBytesTotales + carpeta.tamanoTotal > MAX_TOTAL_SIZE) {
                    setError(`La carpeta "${folderName}" excedería el límite total`);
                    return;
                }
        
                const carpetaId = `${timestamp}:${uuidv4()}`;
                const archivosEnCarpeta = [];
        
                // Process each file in folder
                carpeta.archivos.forEach(({ file, relativePath, size }) => {
                    if (file.size > MAX_FILE_SIZE) {
                        archivosGrandes.push(`${folderName}/${relativePath} (${formatBytes(file.size)})`);
                        return;
                    }
        
                    const archivoId = `${carpetaId}:${uuidv4()}`;
                    
                    archivosEnCarpeta.push({
                        id: archivoId,
                        name: relativePath,
                        size: size,
                        type: "Archivo"
                    });

                    newFilesToUpload.push({ 
                        file: file,
                        type: "Archivo",
                        id: archivoId,
                        carpetaId: carpetaId,
                        fullPath: relativePath
                    });
                    /* newFileList.push({
                        id: fileId,
                        size: file.size,
                        name: file.name,
                        type: "Archivo",
                        status: 'pending' // pending, uploading, success, error
                    }); */

                });
    
                // Add folder entry
                if (archivosEnCarpeta.length > 0) {
                    newFileList.push({
                        id: carpetaId,
                        size: carpeta.tamanoTotal,
                        name: folderName,
                        type: "Carpeta",
                        archivos: archivosEnCarpeta,
                        cantidadArchivos: archivosEnCarpeta.length,
                        status: 'pending' // pending, uploading, success, error

                        /* id: carpetaId,
                        nombre: folderName,
                        tamano: carpeta.tamanoTotal,
                        tipo: "Carpeta",
                        archivos: archivosEnCarpeta,
                        cantidadArchivos: archivosEnCarpeta.length */
                    });
        
                    nuevoBytesTotales += carpeta.tamanoTotal;
                    existingFoldersSet.add(folderName);
                }
            });
    
            // Show alerts
            if (duplicados.length > 0) {
                setAlertaDuplicados({
                    visible: true,
                    archivos: duplicados
                });
            }
    
            if (archivosGrandes.length > 0) {
                setError(`Archivos que exceden el límite: ${archivosGrandes.join(', ')}`);
            }
    
            // Update state
            if (newFileList.length > 0) {
                /* setListaArchivos(prev => [...prev, ...nuevosArchivos]);
                setListaArchivosASubir(prev => {
                    const updated = [...prev, ...nuevosArchivosASubir];
                    setProgresoArchivos(updated.length);
                    return updated;
                }); */
                // Update your state with ALL files
                setFilesToUpload(newFilesToUpload);
                setFileList(prev => [...prev, ...newFileList]);
                setUploadStats(prev => ({
                    ...prev,
                    total: prev.total + newFilesToUpload.length
                }));
                setBytesTotales(nuevoBytesTotales);
            }
    
        } catch (err) {
            console.error('Error al agregar carpeta:', err);
            setError('Ocurrió un error al procesar la carpeta.');
        } finally {
            // Reset input value to allow selecting the same folder again
            if (e.target) {
                e.target.value = '';
            }
        }
    };

    const toggleCarpeta = (id) => {
        setCarpetasExpandidas(prev => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        return newSet;
        });
    };

    const procesarDropCarpeta = async (e) => {
        //console.log('drop', e);
        let fileList = [];
        let drop = e.dataTransfer.items;
        
        async function scanFiles(item, path = '') {
        if (item.isDirectory) {
            let directoryReader = item.createReader();
            
            // readEntries might need multiple calls for large directories
            const readAllEntries = async () => {
            const entries = await new Promise((resolve, reject) => {
                directoryReader.readEntries(resolve, reject);
            });
            
            if (entries.length > 0) {
                // Process this batch
                for (const entry of entries) {
                if (entry.isFile) {
                    const file = await new Promise((resolve, reject) => {
                    entry.file(resolve, reject);
                    });
                    
                    // CRITICAL: Manually set webkitRelativePath to preserve folder structure
                    const relativePath = path + entry.name;
                    Object.defineProperty(file, 'webkitRelativePath', {
                    value: relativePath,
                    writable: false,
                    configurable: true
                    });
                    
                    fileList.push(file);
                } else {
                    // Recurse into subdirectory with updated path
                    await scanFiles(entry, path + entry.name + '/');
                }
                }
                // Check for more entries
                await readAllEntries();
            }
            };
            
            await readAllEntries();
        } else if (item.isFile) {
            const file = await new Promise((resolve, reject) => {
            item.file(resolve, reject);
            });
            
            // Set webkitRelativePath for individual files
            Object.defineProperty(file, 'webkitRelativePath', {
            value: path + item.name,
            writable: false,
            configurable: true
            });
            
            fileList.push(file);
        }
        }
        
        // Process all dropped items
        for (let i = 0; i < drop.length; i++) {
        let item = drop[i].webkitGetAsEntry();
        if (item) {
            // Start with the root folder/file name
            const rootPath = item.isDirectory ? item.name + '/' : '';
            await scanFiles(item, rootPath);
        }
        }
        // Now fileList is complete with proper paths
        folderIconRef.current.className = 'bi bi-folder-plus';
        overlayRef.current.setAttribute('style', '');
        handleFolderSelect(fileList);
    };

    const eliminarItem = (id) => {
        const item = fileList.find(file => file.id === id);
        
        if (!item) return;

        if (item.type === 'Carpeta') {
            // Remove folder and all its files from upload list
            setFilesToUpload(prev => {
                const updated = prev.filter(file => file.carpetaId !== id);
                setUploadStats(prev => ({
                    ...prev,
                    total: updated.length
                }));
                return updated;
            });            
            setFileList(prev => prev.filter(file => file.id !== id));
            setBytesTotales(prev => prev - item.size);
        } else {
            // Remove individual file
            setFileList(prev => prev.filter(file => file.id !== id));
            setFilesToUpload(prev => {
                const updated = prev.filter(file => file.id !== id);
                setUploadStats(prev => ({
                    ...prev,
                    total: updated.length
                }));
                return updated;
            });
            setBytesTotales(prev => prev - item.size);
        }
    };

    // Upload process management
    const startUploadProcess = useCallback(async () => {
        console.log('Starting upload process');
        setUploadStats(prev => ({ ...prev, isUploading: true }));

        let uploadedCount = 0;
        let completedUploads = 0;
        let failed = 0;
        const totalFiles = filesToUpload.length;

        const handleUploadComplete = (fileId, success = true) => {
            setConcurrentUploads(prev => prev - 1);
            completedUploads++;
            if (!success) {
                failed++;
            }
            
            setUploadStats(prev => ({
                ...prev,
                uploaded: completedUploads
            }));
            
            // Mark file status
            setFileList(prev => prev.map(f => 
                f.id === fileId ? { ...f, status: success ? 'success' : 'error' } : f
            ));
            
            // Check if all uploads are complete
            if (completedUploads >= totalFiles) {
                const datosCarga = datosCargaRef.current;
                setUploadStats(prev => ({
                    ...prev,
                    isUploading: false,
                    uploaded: totalFiles
                }));
                validateUploads?.(failed, datosCarga, bytesTotales);
            }
        };

        while (uploadedCount < totalFiles) {
            if (concurrentUploads < MAX_CONCURRENT_UPLOADS) {
                const fileToUpload = filesToUpload[uploadedCount];
                setConcurrentUploads(prev => prev + 1);
                
                // Mark file as uploading
                setFileList(prev => prev.map(f => 
                    f.id === fileToUpload.id ? { ...f, status: 'uploading' } : f
                ));
                
                // Determine upload method based on file size
                const uploadPromise = fileToUpload.file.size > LARGE_FILE_THRESHOLD
                    ? uploadLargeFile(fileToUpload.file, fileToUpload.id, fileToUpload.fullPath)
                    : uploadFile(fileToUpload.file, fileToUpload.id, fileToUpload.fullPath);
                
                // Don't await here to allow concurrent uploads
                uploadPromise
                    .then(() => {
                        handleUploadComplete(fileToUpload.id, true);
                    })
                    .catch(error => {
                        console.error('Upload failed:', error);
                        handleUploadComplete(fileToUpload.id, false);
                    });
                
                uploadedCount++;
            } else {
                await sleep(200);
            }
        }
    }, [filesToUpload, concurrentUploads, uploadStats.uploaded, uploadStats.total]);

    // Upload large files using chunked method
    const uploadLargeFile = async (file, fileId, fullPath = '') => {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        let currentChunk = 0;
        
        while (currentChunk < totalChunks) {
            const start = currentChunk * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);
            
            await uploadChunk({
                chunk,
                chunkIndex: currentChunk,
                totalChunks,
                fileName: file.name,
                fileId,
                fullPath
            });
            
            // Update progress for this file
            const progress = Math.round(((currentChunk + 1) / totalChunks) * 100);
            setFileList(prev => prev.map(f => 
                f.id === fileId ? { ...f, progress } : f
            ));
            
            currentChunk++;
            
            // Optional: Small delay between chunks to prevent overwhelming the server
            if (currentChunk < totalChunks) {
                await sleep(100);
            }
        }
    };

    const uploadChunk = async ({
        chunk,
        chunkIndex,
        totalChunks,
        fileName,
        fileId,
        fullPath
    }) => {
        let retries = 3;
        
        while (retries > 0) {
            try {
                const datosCarga = datosCargaRef.current;
                const formData = new FormData();
                
                const chunkData = {
                    id_carpeta: datosCarga.id_carpeta,
                    ubicacion: datosCarga.ubicacion,
                    nombreArchivo: fileName,
                    fullPath: fullPath,
                    chunk: chunkIndex,
                    chunks: totalChunks,
                    t_seg: tokenSeguridad           
                };
                console.log('chunkData', chunkData);
                
                // Encrypt all fields
                const encryptedData = {};
                for (const [key, value] of Object.entries(chunkData)) {
                    encryptedData[key] = aesEncryption.cifrar(value.toString()).datos;
                }
                
                // Add encryption keys
                const encryptedKey = await rsaEncryption.cifrar(aesEncryption.llave, publicKeyRSA);
                encryptedData.ivCifradoSimetrico = aesEncryption.iv;
                encryptedData.keyCifradoSimetrico = encryptedKey;
                
                // Create query string from encrypted data
                const queryString = new URLSearchParams(encryptedData);
                
                const response = await axios.post(`${BASE_URL}/cratos/ModuloArchivos/PHP/Implementaciones/subirArchivoG.php`,
                    chunk,
                    {
                        params: queryString,
                        headers: { "Content-Type": "application/octet-stream" },
                        maxRedirects: 0,
                    }
                );
                
                if (response.status === 200 && response.data.resultado === 'ok') {
                    return; // Success
                } else if (response.data.resultado === "YA_SUBIDO") {
                    return; // Already uploaded
                } else {
                    throw new Error(response.data.error?.msj || 'Upload failed');
                }
                
            } catch (error) {
                console.error(`Chunk ${chunkIndex}/${totalChunks} upload error (attempt ${4 - retries}/3):`, error);
                retries--;
                
                if (error.code === "ERR_NETWORK" && retries > 0) {
                    await sleep(7000); // Wait 7 seconds before retry on network error
                } else if (retries === 0) {
                    throw new Error(`Failed to upload chunk ${chunkIndex}/${totalChunks} of ${fileName}: ${error.message}`);
                } else {
                    throw error; // Non-network errors fail immediately
                }
            }
        }
    };

    // Individual file upload
    const uploadFile = useCallback(async (file, fileId, fullPath) => {
        console.log('Uploading file:', file.name, 'with ID:', fileId);
        setUploadStats(prev => ({ ...prev, currentFileName: file.name }));

        const datosCarga = datosCargaRef.current;

        // Add validation check
        if (!datosCarga || !datosCarga.ubicacion || !datosCarga.id_carpeta) {
            console.error('Missing required upload data:', datosCarga);
            throw new Error('Upload data is incomplete');
        }

        const formData = new FormData();
        
        // Add form data
        /* const sessionData = getSessionData();
        const userData = {
        ...sessionData,
        t_seg: tokenSeguridad,
        ubicacion: ubicacion,
        lastModified: file.lastModified.toString()
        }; */
    
        const userData = {
            id_carpeta: datosCarga.id_carpeta,
            ubicacion: datosCarga.ubicacion,
            nombreArchivo: file.name,
            fullPath: fullPath,
            t_seg: tokenSeguridad           
        };
        console.log(userData);
        // Encrypt data
        for (const [key, value] of Object.entries(userData)) {
            formData.append(key, aesEncryption.cifrar(value).datos);
        }

        // Add encryption keys
        const encryptedKey = await rsaEncryption.cifrar(aesEncryption.llave, publicKeyRSA);
        formData.append("ivCifradoSimetrico", aesEncryption.iv);
        formData.append("keyCifradoSimetrico", encryptedKey);
        formData.append("archivo", file);

        let attempts = 0;
        
        while (attempts < RETRY_ATTEMPTS) {
        try {
            const response = await axios.post(`${BASE_URL}/cratos/ModuloArchivos/PHP/Implementaciones/subirArchivo.php`,
            formData,
            {
                onUploadProgress: (progressEvent) => {
                const progress = Math.round((100 * progressEvent.loaded) / progressEvent.total);
                setUploadStats(prev => ({ ...prev, currentProgress: progress }));
                },
                maxRedirects: 0,
            }
            );

            if (response.status === 200 && response.statusText === 'OK') {
                const data = response.data;
                if (parseInt(data) !== 0) {
                    return Promise.resolve();
                }
            }
            
            throw new Error(`Upload failed with status: ${response.status}`);
            
        } catch (error) {
            attempts++;
            console.error(`Upload attempt ${attempts} failed:`, error);
            
            if (attempts >= RETRY_ATTEMPTS) {
                throw error;
            }
            
            if (error.code === "ERR_NETWORK") {
                await sleep(RETRY_DELAY * attempts); // Exponential backoff
            }
        }
        }
    }, [tokenSeguridad, publicKeyRSA]);

    // Retry file upload
    const retryFailedUploads = useCallback(() => {
        const failedFiles = fileList.filter(f => f.status === 'error');
        
        if (failedFiles.length === 0) return;
        
        // Find the original file objects
        const filesToRetry = failedFiles.map(failedFile => {
            return filesToUpload.find(f => f.id === failedFile.id);
        }).filter(Boolean);
        
        // Reset their status to pending
        setFileList(prev => prev.map(f => 
            f.status === 'error' ? { ...f, status: 'pending' } : f
        ));
        
        // Replace upload queue with only failed files
        setFilesToUpload(filesToRetry);
        // Reset upload stats for retry
        setUploadStats(prev => ({
            uploaded: prev.uploaded - failedFiles.length,
            total: prev.total,
            isUploading: false,
            currentProgress: 0,
            currentFileName: ''
        }));
        setTimeout(() => {
            startUploadProcess();
        }, 100);
        
    }, [fileList, filesToUpload]);

    // Add a "Clear All" button to clean up everything
    const clearCompletedUploads = useCallback(() => {
        // Remove successful uploads
        const remainingFiles = fileList.filter(f => f.status === 'error' || f.status === 'pending');
        
        if (remainingFiles.length === 0) {
            // Everything is done, clear everything
            setFileList([]);
            setFilesToUpload([]);
            setUploadStats({
                uploaded: 0,
                total: 0,
                isUploading: false,
                currentProgress: 0,
                currentFileName: ''
            });
            hideUploadWindow({ preventDefault: () => {} });
        } else {
            // Keep only failed/pending files
            setFileList(remainingFiles);
            const remainingToUpload = filesToUpload.filter(f => 
                remainingFiles.some(rf => rf.id === f.id)
            );
            setFilesToUpload(remainingToUpload);
            setUploadStats(prev => ({
                ...prev,
                uploaded: 0,
                total: remainingFiles.length
            }));
        }
    }, [fileList, filesToUpload]);
    // UI event handlers
    const handleFolderClick = useCallback((e) => {
        e.preventDefault();
        folderInputRef.current?.click();
    }, []);

    const handleFileClick = useCallback((e) => {
        e.preventDefault();
        fileInputRef.current?.click();
    }, []);

    const toggleUploadList = useCallback((show) => {
        if (uploadListRef.current) {
            uploadListRef.current.className = show ? "card-body p-0" : "d-none";
        }
    }, []);

    const hideUploadWindow = useCallback((e) => {
        e.preventDefault();
        // Everything is done, clear everything
        setFileList([]);
        setFilesToUpload([]);
        setUploadStats({
            uploaded: 0,
            total: 0,
            isUploading: false,
            currentProgress: 0,
            currentFileName: ''
        });
        if (uploadWindowRef.current) {
            uploadWindowRef.current.className = "d-none";
        }
    }, []);
    
    return (
        <>
        <div ref={overlayRef} className='row'>
          <div className="col-xs-12 col-md-6 mb-3">
            <div ref={fileAreaRef} className='card border border-dark' id="agregarArchivo" style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.preventDefault();
                fileInputRef.current.click();
              }}
              onMouseOver={(e) => {
                e.stopPropagation();
                e.preventDefault();
                fileAreaRef.current.className = 'card bg-secondary border border-dark shadow';
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
                e.preventDefault();
                fileAreaRef.current.className = 'card border border-dark';
              }}
              onDragEnter={(e) => { e.stopPropagation(); e.preventDefault(); } }
              onDragOver={(e) => {
                e.stopPropagation();
                e.preventDefault();
                fileAreaRef.current.className = 'card bg-secondary border border-dark shadow';
              }}
              onDragLeave={(e) => {
                e.stopPropagation();
                e.preventDefault();
                fileAreaRef.current.className = 'card border border-dark';
              }}
              onDrop={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleFileSelection(e.dataTransfer.files);
              }}
            >
              <div className="card-body text-center">
                <i className="bi bi-file-earmark-plus" style={{ fontSize: '80px' }}></i>
                <p>Agregar o soltar un archivo</p>
              </div>
            </div>
            <input
              ref={fileInputRef}
              id="fileInput"
              type="file"
              //className="form-control"
              className="visually-hidden"
              multiple
              onChange={handleFileSelection} />
          </div>
          <div className="col-xs-12 col-md-6 mb-3">
            <div ref={folderAreaRef} className='card border border-dark' id='agregarCarpeta' style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.preventDefault();
                folderInputRef.current.click();
              }}
              onMouseOver={(e) => {
                e.stopPropagation();
                e.preventDefault();
                folderAreaRef.current.className = 'card bg-secondary border border-dark shadow';
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
                e.preventDefault();
                folderAreaRef.current.className = 'card border border-dark';
              }}
              onDragEnter={(e) => { e.stopPropagation(); e.preventDefault(); } }
              onDragOver={(e) => {
                e.stopPropagation();
                e.preventDefault();
                folderAreaRef.current.className = 'card bg-secondary border border-dark shadow';
              }}
              onDragLeave={(e) => {
                e.stopPropagation();
                e.preventDefault();
                folderAreaRef.current.className = 'card border border-dark';
              }}
              onDrop={(e) => {
                e.stopPropagation(); e.preventDefault();
                folderIconRef.current.className = 'bi bi-hourglass';
                overlayRef.current.setAttribute('style', "width: 100%; height: 100%; z-index: 10000; pointer-events: none;");
                procesarDropCarpeta(e);
              }}
            >
              <div className="card-body text-center pe-auto">
                <i ref={folderIconRef} className="bi bi-folder-plus" id="iconoCarpeta" style={{ fontSize: '80px' }}></i>
                <p>Agregar carpeta</p>
              </div>
            </div>
            <input
              ref={folderInputRef}
              id="inputCarpeta"
              type="file"
              className="visually-hidden"
              multiple
              onChange={(e) => {
                e.preventDefault();
                handleFolderSelect(e);
              } } />
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            {/* Error Alert */}
            {error && (
              <div className="alert alert-warning alert-dismissible fade show" role="alert">
                {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label="Close"
                ></button>
              </div>
            )}

            {/* Duplicate Files Alert */}
            {alertaDuplicados.visible && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <strong>Archivos duplicados:</strong>
                <ul className="mb-0 mt-2">
                  {alertaDuplicados.archivos.map((nombre, index) => (
                    <li key={index}>{nombre}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setAlertaDuplicados({ visible: false, archivos: [] })}
                  aria-label="Close"
                ></button>
              </div>
            )}
            {/* File Statistics */}
            <div className="mb-3">
                <p>
                    <strong>Archivos:</strong> {uploadStats.uploaded} archivos de {uploadStats.total} |
                    <strong> Tamaño total:</strong> {formatBytes(bytesTotales)} / {formatBytes(MAX_TOTAL_SIZE)}
                </p>
                <p>
                    {uploadStats.isUploading && (
                        <>
                        {` ${uploadStats.currentProgress}%`}
                        <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                        </>
                    )}
                </p>
                <div className="card-footer p-2 d-flex justify-content-between">
                    <div>
                        {fileList.some(f => f.status === 'error') && (
                            <button 
                                className="btn btn-sm btn-warning"
                                onClick={retryFailedUploads}
                                disabled={uploadStats.isUploading}
                            >
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                Reintentar fallidos
                            </button>
                        )}
                    </div>
                    {/* <div>
                        {!uploadStats.isUploading && (  
                            <button 
                                className="btn btn-sm btn-secondary"
                                onClick={clearCompletedUploads}
                            >
                                <i className="bi bi-check2-all me-1"></i>
                                Limpiar completados
                            </button>
                        )}
                    </div> */}
                </div>
            </div>

            {/* File/Folder List */}
            {fileList.length > 0 && (
              <div className="list-group">
                {fileList.map(item => (
                  <div key={item.id}>
                    <div className="list-group-item d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center flex-grow-1">
                            {item.type === 'Carpeta' && (
                            <button
                                className="btn btn-sm btn-link text-decoration-none p-0 me-2"
                                onClick={() => toggleCarpeta(item.id)}
                            >
                                {carpetasExpandidas.has(item.id) ? '📂' : '📁'}
                            </button>
                            )}
                            {item.type === 'Archivo' && <span className="me-2">📄</span>}                            
                            <div>
                                <strong>{item.name}</strong>
                                {item.type === 'Carpeta' && (
                                    <span className="text-muted ms-2">
                                    ({item.cantidadArchivos} archivo{item.cantidadArchivos !== 1 ? 's' : ''})
                                    </span>
                                )}
                                <br />
                                <small className="text-muted">{formatBytes(item.size)}</small>
                                <span className={`badge ${
                                    item.status === 'pending' ? 'text-secondary' :
                                    item.status === 'uploading' ? 'text-warning' :
                                    item.status === 'success' ? 'text-success' :
                                    'text-danger'
                                    }`}>
                                    {item.status === 'pending' && '⏳'}
                                    {item.status === 'uploading' && '📤'}
                                    {item.status === 'success' && '✅'}
                                    {item.status === 'error' && '❌'}
                                </span>
                                {item.status === 'uploading' && (
                                    <span className="spinner-border spinner-border-sm ms-1" role="status" aria-hidden="true"></span>
                                )}
                            </div>
                        </div>
                        
                        <button
                            className="btn btn-sm btn-danger"
                            onClick={() => eliminarItem(item.id)}
                        >
                            Eliminar
                        </button>
                    </div>

                    {/* Show folder contents when expanded */}
                    {item.type === 'Carpeta' && carpetasExpandidas.has(item.id) && (
                      <div className="ps-4 border-start ms-3">
                        {item.archivos.map(archivo => (
                          <div
                            key={archivo.id}
                            className="list-group-item list-group-item-light d-flex justify-content-between align-items-center"
                          >
                            <div>
                              <span className="me-2">📄</span>
                              {archivo.name}
                              <br />
                              <small className="text-muted">{formatBytes(archivo.size)}</small>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>        
        </>
    );
};

export default SubirArchivosIndex;